import { Component, Inject, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ContentService, HrefResolverService } from '@blk/cwp-ng-framework';
import { DzModalRef } from '@blk/daisy/modules/modals';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ReportData, ReportGenerationService } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, APPLICATION_NAME_TOKEN, ApplicationName, OutputDesignation, PDFDataErrorDCR, ViewCategory } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, PcConfigKey, PdfSharedConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, ComponentData } from '@ddt-web/data-service';
import { ApplicationNames, BpasClientOutput, ClientOutput, OutputFlag, SharedApplicationConstants } from '@ddt-web/domain';
import { EmailDomainCustomizationFlags, EmailDomainCustomizationService } from '@ddt-web/email-domain-customization';
import { ErrorPopupService, PopupDialogAction, PopupDialogActionOptions, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { StatusColour } from '@ddt-web/fund-stats';
import { AchoReportComposerService, AchoReportConfiguration, SvgSanitizer } from '@ddt-web/pdf-utils';
import { ApplicationState, ApplicationStateStoreProxyService, ProposalPurpose } from '@ddt-web/shared-stores';
import { Config, StoreService } from '@ddt-web/store-service';
import { SharedQueryParamKey, UrlRedirectService, UrlService, UrlUtils } from '@ddt-web/url';
import { UserDataService } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as dayjs from 'dayjs';
import * as camelCase from 'lodash/camelCase';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { filter, first, flatMap, map, take, timeout } from 'rxjs/operators';

import { PdfGenerateStoreConfig } from '../../config/store.config';
import { PdfReportConfig } from '../../models/pdf-report-config.interface';
import { ProspectusEmailService } from '../../services/prospectus-email.service';
import { ReportConfigService } from '../../services/report-config.service';

@UntilDestroy()
@Component({
	selector: 'ac-pdf-generate',
	templateUrl: './pdf-generate.component.html',
	styleUrls: ['./pdf-generate.component.scss'],
	providers: []
})
export class PdfGenerateComponent implements OnInit, OnChanges, OnDestroy {

	// ------------------------
	// Store Variables
	// ------------------------
	public yieldConfig: Object;
	public name: string;
	public firm: string;
	public description: string;
	public reportType: string;
	public showScenarioYield: boolean = false;
	public hideYield: boolean = false;

	public showDollarAmountAsDefault: boolean = false;
	public enableDollarAmountToggle: boolean = false;

	public enableModularity: boolean = true;
	public isBlkModel: boolean = true;
	public isPerformanceDataValid: boolean = true;
	public performanceErrorMessage: string;

	public outputFlags: OutputFlag[] = [
		new OutputFlag('includeRisk', true),
		new OutputFlag('includeYield', true),
		new OutputFlag('showDollarAmountAsDefault', true),
		new OutputFlag('includeScenarioYield', true),
		new OutputFlag('includeTaxes', true),
		new OutputFlag('includeEtfComp', true),
		new OutputFlag('includeFees', true),
		new OutputFlag('red', true),
		new OutputFlag('yellow', true),
		new OutputFlag('green', true)
	];
	public portfolioStrategist: string = '';
	public engagementUniqueId: string = '';

	@InjectConfig(SharedConfigKey.SHOW_FUND_PROSPECTUS) private readonly showFundProspectus: boolean = false;
	@InjectConfig(PcConfigKey.ENABLE_NEW_CLIENT_REPORT_FUNCTIONALITY) public readonly enableNewClientReport = false;

	// ------------------------
	// Content Service Variables
	// ------------------------

	public privacyUrl: string;

	// ------------------------
	// Local Variables
	// ------------------------
	public readonly ACT33TYPES = { 'Grantor Trust': true, 'Partnership (3C1)': true };
	public readonly failedMessage = PopupDialogObject.create({
		success: false,
		title: this.contentService.get('FAIL_DIALOG_HEADING', 'Failed!'),
		description: this.contentService.get('PDF_DATA_RETRIEVAL_ERROR', PDFDataErrorDCR.PDF_DATA_RETRIEVAL_ERROR),
		actions: [
			PopupDialogAction.create({
				description: 'Try refresh page',
				label: 'Refresh',
				reload: true
			} as PopupDialogActionOptions)
		]
	} as PopupDialogOptions);
	public client: string;
	public reportTypeText: string;
	public model: ClientOutput;
	public emailSent: boolean = false;
	public requiredActions: boolean = false;
	public emailNotSentError: boolean = false;
	public disableYield: boolean = false;
	public disableGenerateReport: boolean = false;
	public canGenerateReport: boolean = true;
	public includeYield: boolean = false;
	public includeScenarioYield: boolean = false;

	public includeCustomization: boolean = false;
	public includeSnapshotCustomization: boolean = true;

	public inputForm: NgForm;
	public pdfDebug: boolean = false;
	public reportPages: Array<string> = null;
	public reportCover: string;
	public advisorLogo: string = ' ';
	public disableAdvisorLogo: boolean = true;
	public applicationNameText: string;
	public firmNameRequired: boolean = false;
	public ViewCategory = ViewCategory;
	public isLandscape: boolean;
	public showLogoError: boolean;
	private hasValidAdvisorLogo: boolean;

	public reportVisibilityConfig: PdfReportConfig;
	public snapshotVisibilityConfig: PdfReportConfig;

	public achoReportConfiguration: AchoReportConfiguration;
	public showGenerateActions: boolean;
	public exportAsPNG: boolean;
	public enableYieldOptions: boolean;
	public includeBenchmark: boolean = false;
	public isBenchmarkAvailable: boolean = false;

	public isLoading: boolean = true;
	public componentDataMapping$: Observable<Map<string, ComponentData>>;

	public isPacApp: boolean = false;
	public showAdditionalHeader: boolean = false;
	public additionalHeaderInfo: string;
	public iqFundStatuses: Map<string, boolean>;

	public purposeState: ProposalPurpose;
	public showPurposeValidation: boolean = false;
	public isProposalSnapshotLoadingComplete: boolean = false;
	public isProposalLoadingComplete: boolean = false;
	public showRiskProfileModularity: boolean = false;

	// ------------------------
	// Config layer Variables
	// ------------------------

	@InjectConfig(PdfSharedConfigKey.REPORT_DOWNLOAD_SHOW_PRIVACY_DISCLAIMER) public readonly showPrivacyDisclaimer: boolean = true;

	@InjectConfig(PdfSharedConfigKey.ENABLE_PDF_ACTIONS_DRAWER) public readonly enableActionsDrawer = false;
	@InjectConfig(PdfSharedConfigKey.USE_CLIENT_NAME_FOR_FILENAME) private readonly useClientNameForFilename = false;

	@InjectConfig(PdfSharedConfigKey.ENABLE_YIELD) private readonly enableYield: boolean = false;
	@InjectConfig(PdfSharedConfigKey.ACT_33_ENABLED) private readonly isAct33Enabled: boolean = false;
	// eslint-disable-next-line regex/invalid
	@InjectConfig(PdfSharedConfigKey.ENABLE_CUSTOMIZATION) public isCustomizationAvailable: boolean = false;
	@InjectConfig(PdfSharedConfigKey.SHOULD_SHOW_STATUS_FILTERING) public readonly shouldShowStatusFiltering: boolean = false;
	@InjectConfig(PdfSharedConfigKey.SHOW_COBRANDING_DISCLOSURE) private readonly showCoBrandingDisclosure: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.PDF_RISK_PROFILE_MODULARITY_FAMILIES) private readonly pdfRiskProfileModularityFamiliesList = [];

	constructor(
		private userDataService: UserDataService,
		private contentService: ContentService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private urlService: UrlService,
		private urlRedirectService: UrlRedirectService,
		private storeService: StoreService,
		private prospectusEmailService: ProspectusEmailService,
		private errorPopupService: ErrorPopupService,
		private hrefResolverService: HrefResolverService,
		private reportConfigService: ReportConfigService,
		private appStateStoreProxyService: ApplicationStateStoreProxyService,
		private reportComposer: AchoReportComposerService,
		private reportGenerationService: ReportGenerationService,
		private emailDomainCustomizationService: EmailDomainCustomizationService,
		private route: ActivatedRoute,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) { }


	// ------------------------
	// Angular Lifecycle Hooks
	// ------------------------

	public ngOnInit(): void {
		// This will allow to preview the PDF in the web preview in case of debugging in PROD
		// and defaults for local dev mode to speed up the dev process
		this.pdfDebug =
			get(window, 'location.href', []).includes('showEditor=true') ||
			get(window, 'location.href', []).includes('pdfPreview') ||
			get(window, 'location.hostname') === 'localhost';
		const defaultPrivacyUrl = this.hrefResolverService.getResolvedUrl('privacyUrl');
		this.privacyUrl = this.contentService.get('PRIVACY_URL', defaultPrivacyUrl);

		this.showRiskProfileModularity = this.isFamilyIdEnabledForRiskProfileModularity();

		this.initBenchmark();

		this.setApplicationText();

		combineLatest([this.getReportType$(), this.getUserDetails$(), this.getFirmNameCustomization$()])
			.pipe(untilDestroyed(this))
			.subscribe(([reportType, profile, shouldKeepFirmNameBlank]) => {
				if (!reportType) {
					if (isEmpty(this.route.snapshot.queryParams)) {
						this.urlRedirectService.redirectToRoute('/');
					} else {
						this.redirectToTopOfMainPage();
					}
				} else {
					this.reportType = reportType;
					if (reportType === ViewCategory.FA_PLUS_PDF) {
						// for engagement portfolios
						this.getPSConfig$()
							.pipe(take(1))
							.subscribe(engagement => {
								this.model = new ClientOutput(
									engagement.title,
									engagement.advisorName,
									shouldKeepFirmNameBlank ? '' : engagement.firmName,
									[new OutputFlag('includeYield', true)],
									engagement.portfolioStrategist,
									engagement.uniqueId
								);
							});
					}
					const defaultFirmName = !shouldKeepFirmNameBlank ? get(profile, 'firmName', '') : '';
					this.name = get(this.model, 'clientName', get(profile, 'fullName', ''));
					// always use the firmname from model, if the model exists and has firm property - even if it is empty
					this.firm = this.model?.firm ?? defaultFirmName;
					this.description = get(this.model, 'advisorName', '');
					this.portfolioStrategist = get(this.model, 'portfolioStrategist', '');
					this.engagementUniqueId = get(this.model, 'uniqueID', '');
					this.reset();
					this.reportCover = this.reportConfigService.getReportCover(this.reportType, this.model);
					this.achoReportConfiguration = this.reportConfigService.getReport(reportType, this.model.outputFlags);
					const dimensions = get(this.achoReportConfiguration, 'pageTemplate.dimensions', { pageWidth: 0, pageHeight: 0 });
					this.isLandscape = dimensions.pageWidth > dimensions.pageHeight;
					if (this.applicationName === ApplicationNames.PORTFOLIO_CENTRE_360) {
						if (!this.enableNewClientReport) {
							this.reportTypeText = this.contentService.get('GENERATE_CLIENT_REPORT', 'GENERATE CLIENT REPORT');
						} else {
							if (reportType === ViewCategory.CLIENT_PDF) {
								this.reportTypeText = this.contentService.get('CLIENT_REPORT_PDF', 'CLIENT_REPORT_PDF');
							} else {
								this.reportTypeText = this.contentService.get('ADVISOR_REPORT_PDF', 'ADVISOR_REPORT_PDF');
							}
						}
					} else {
						if (reportType === ViewCategory.CLIENT_PDF) {
							this.reportTypeText = this.contentService.get('GENERATE_CLIENT_REPORT', 'GENERATE CLIENT REPORT');
						} else if (reportType === ViewCategory.PROPOSAL_PDF) {
							this.reportTypeText = this.contentService.get('GENERATE_PROPOSAL_REPORT', 'GENERATE PROPOSAL REPORT');
						} else {
							this.reportTypeText = this.contentService.get('GENERATE_ADVISOR_REPORT', 'GENERATE ADVISOR REPORT');
						}
					}
					if (this.enableYield) {
						this.enableYieldOptions = (this.reportType === ViewCategory.FA_SELF_SERVICE_PDF && this.applicationName === ApplicationNames.MODELS_BROWSE)
							|| this.reportType === ViewCategory.PROPOSAL_PDF
							|| (this.reportType === ViewCategory.CLIENT_PDF
								&& this.applicationName !== ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION
								&& !SharedApplicationConstants.PC_APPS.includes(this.applicationName)
							);
					}
				}
			});

		// proposal state validation
		this.appStateStoreProxyService.getProposalState().pipe(untilDestroyed(this)).subscribe(state => {
			this.purposeState = state?.purpose;
			this.clear();
		});

		if (this.enableYield) {
			this.generateYieldConfig$()
				.pipe(take(1))
				.subscribe(yieldConfig => {
					this.yieldConfig = yieldConfig;
					this.clear();
				});

			this.getScenarioYieldFlag$()
				.pipe(
					filter(value => value),
					take(1)
				)
				.subscribe(visibilityObject => {
					this.showScenarioYield = visibilityObject.showScenarioYield;
					this.hideYield = visibilityObject.hideYield;
				});
		} else {
			// firm name is required for PAC/PC apps and optional for all other apps
			this.firmNameRequired = true;
		}

		this.getReportConfigData$()
			.pipe(
				filter(value => value),
				take(1)
			)
			.subscribe(data => {
				this.includeYield = data.includeYield || data.includeScenarioYield;
				this.includeScenarioYield = data.includeScenarioYield;
				this.showDollarAmountAsDefault = data.showDollarAmountAsDefault;
				this.enableDollarAmountToggle = data.enableDollarAmountToggle;
				this.enableModularity = data.enableModularity ?? true;
				this.isPerformanceDataValid = data.isPerformanceDataValid;
				this.isBlkModel = data.isBlkModel;
				if ([ApplicationNames.MODELS_BROWSE, ApplicationNames.MODELS_EXPERIENCE].includes(this.applicationName) && this.enableModularity) {
					// this depends on if its a blk model or not
					this.isCustomizationAvailable = true;
				}
			});

		this.componentDataMapping$ = this.reportComposer.getComponentDataMapping(
			this.achoReportConfiguration,
			this.contentService,
			null,
			this.achoReportConfiguration?.componentReqIdMapping
		).pipe(
			filter(mapping => this.areAllComponentDataAvailable(mapping)),
			timeout(SharedApplicationConstants.DEFAULT_REQUEST_TIMEOUT),
			first()
		);

		this.componentDataMapping$
			.pipe(take(1))
			.subscribe(() => this.isLoading = false);

		this.requiredActions = this.requiredActionPerformed();
		this.reset();
	}

	public ngOnChanges(): void {
		this.disableYield = this.yieldConfig ? !this.yieldConfig['holdingsCommunication'] : false;
		this.disableGenerateReport = this.yieldConfig ? !this.yieldConfig['act33HoldingsCommunication'] : false;
		this.reset();
	}

	public ngOnDestroy(): void {
		let reportConfigClone;
		this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig)
			.pipe(take(1))
			.subscribe(state => {
				reportConfigClone = cloneDeep(state);
				reportConfigClone = { ...reportConfigClone, customPages: {} };
			});
		this.storeService.upsertOne(PdfGenerateStoreConfig.reportConfig, reportConfigClone);
	}

	private initBenchmark(): void {
		this.updateBenchmarkAvailability(!isEmpty(this.urlService.getAcBenchmark()));

		const engagementId = this.urlService.queryParams?.[SharedQueryParamKey.ENGAGEMENT];
		if (engagementId) {
			this.storeService.selectById(this.storeConfig.engagementPortfolios, engagementId)
				.pipe(first(engagement => engagement?.[engagementId]))
				.subscribe(engagement => {
					const children = engagement?.[engagementId]?.children ?? [];
					for (const child of children) {
						if (child?.outputDesignation === OutputDesignation.BENCHMARK) {
							this.updateBenchmarkAvailability(true);
						}
					}
				});
		}
	}

	private updateBenchmarkAvailability(isBenchmarkAvailable): void {
		this.isBenchmarkAvailable = isBenchmarkAvailable;
		if (this.isBenchmarkAvailable) {
			this.includeBenchmark = true;
			this.outputFlags.push(new OutputFlag('includeBenchmark', this.includeBenchmark));
		}
	}

	private areAllComponentDataAvailable(mapping: Map<string, ComponentData>): boolean {
		for (const componentData of Array.from(mapping.values())) {
			if (componentData.isLoading) {
				return false;
			}
		}

		return true;
	}

	private reset(): void {
		if (this.isPacApp) {
			this.model = this.model instanceof BpasClientOutput ?
				new BpasClientOutput(this.model.suite, this.model.clientName, this.model.audience) :
				new BpasClientOutput();
		} else {
			this.model = new ClientOutput(this.name, this.description, this.firm, this.outputFlags, this.portfolioStrategist, this.engagementUniqueId);
		}
		this.emailSent = false;
		this.clear();
	}

	private clear(): void {
		this.emailNotSentError = false;
		this.requiredActions = this.requiredActionPerformed();
		this.canGenerateReport = this.checkGenerateReport();
	}

	/**
	 * Checks for valid logo, disclosure, inputForm and required actions are true, returns true
	 * Checks if it is FA_PLUS_PDF return true
	 * Checks if it is a FA_SELF_SERVICE_PDF, has valid InputForm and is not a Mval report, returns true
	 * Checks if it is a FA_SELF_SERVICE_PDF, has valid InputForm and is a Mval report and requiredActions are true, returns true
	 * Checks if it is a FA_SELF_SERVICE_PDF, has valid InputForm and is a Mval report and requiredActions are false, returns false
	 * All other cases returns false
	 * @returns {boolean}
	 */

	private checkGenerateReport(): boolean {
		const isProposalValid = this.reportType !== ViewCategory.PROPOSAL_PDF || (this.isProposalLoadingComplete && this.isProposalSnapshotLoadingComplete);
		if (!isProposalValid) {
			return false;
		}
		const isValidLogo = Boolean(this.disableAdvisorLogo || (!this.disableAdvisorLogo && this.advisorLogo.trim()));
		const checkFormFields = this.inputForm?.valid && this.requiredActions && isValidLogo;
		if (checkFormFields) {
			if (this.applicationName === ApplicationNames.MODELS_BROWSE) {
				const isPerformanceValid = this.checkGenerateReportForMval();
				return isPerformanceValid;
			}
			return checkFormFields;
		}

		if (this.reportType === ViewCategory.FA_PLUS_PDF) {
			return true;
		}

		if (this.reportType === ViewCategory.FA_SELF_SERVICE_PDF && this.inputForm?.valid) {
			if (this.applicationName === ApplicationNames.MODELS_BROWSE) {
				const isPerformanceValid = this.checkGenerateReportForMval();
				return this.requiredActions && isPerformanceValid;
			}
			return true;
		}
		return false;
	}

	private checkGenerateReportForMval(): boolean {
		const isPerformanceValid =
			(this.reportVisibilityConfig && !this.reportVisibilityConfig?.sections?.find(section => section.nameKey === 'FAMILY_PERFORMANCE_KEY')?.checked)
			|| this.isPerformanceDataValid;
		this.performanceErrorMessage = isPerformanceValid ? '' :
			this.contentService.get(
				'MVAL_PDF_PERFORMANCE_ERROR_MESSAGE',
				'Client PDF is unavailable due to an issue with the performance data. Please try again later.');
		return isPerformanceValid;
	}

	// ------------------------
	// Report Config
	// ------------------------

	private getReportType$(): Observable<string> {
		return this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).pipe(
			map((data: any) => (data && data.reportType ? data.reportType : null)),
			untilDestroyed(this)
		);
	}

	private getScenarioYieldFlag$(): Observable<any> {
		return this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).pipe(
			map((data: any) => (data && data.yieldVisibility ? data.yieldVisibility : null)),
			untilDestroyed(this)
		);
	}

	private getReportConfigData$(): Observable<any> {
		return this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).pipe(
			untilDestroyed(this)
		);
	}

	private getUserDetails$(): Observable<any> {
		return this.userDataService.getUserProfile().pipe(untilDestroyed(this));
	}

	private getFirmNameCustomization$(): Observable<boolean> {
		return this.emailDomainCustomizationService.isFeatureEnabledOnFirm(EmailDomainCustomizationFlags.KEEP_FIRM_NAME_BLANK).pipe(untilDestroyed(this));
	}

	private getPSConfig$(): Observable<any> {
		const id = this.urlService.getEngagementId();
		return this.storeService
			.selectAll(PdfGenerateStoreConfig.engagementPortfolios).pipe(
				filter(store => store && store.entities && !store.isLoading),
				map(store => store.entities[id]),
				untilDestroyed(this));
	}

	// ------------------------
	// Yield Config
	// ------------------------

	private generateYieldConfig$(): Observable<any> {
		const ids = this.urlService.getEngagementId();
		if (ids) {
			return this.storeService.selectAll(PdfGenerateStoreConfig.positionDetails).pipe(
				filter(data => this.isAct33Enabled),
				map(data => (data && !data.isLoading && data.entities ? data.entities : [])),
				map(portfolios => this.getYieldConfig(portfolios)),
				untilDestroyed(this)
			);
		} else {
			return this.urlService.getModelFamilyIdsOrPortfolioIds().pipe(
				flatMap(portfolioIds => {
					if (!isEmpty(portfolioIds)) {
						return this.storeService.selectByIds(PdfGenerateStoreConfig.positionDetails, portfolioIds).pipe(
							filter(data => this.isAct33Enabled),
							map(data => (data && !data.isLoading && data.entities ? data.entities : [])),
							map(portfolios => this.getYieldConfig(portfolios)),
							untilDestroyed(this)
						);
					}

					return EMPTY;
				})
			);
		}
	}

	private getYieldConfig(portfolios): any {
		const holdings = [];
		const act33Holdings = [];
		const act33Filter = ['ASSET_TYPE_FUND'];
		Object.values(portfolios).forEach(portfolio => {
			portfolio['children']
				.filter(holding => act33Filter.includes(holding.assetType))
				.forEach(holding => {
					if (!holdings[holding['cusip']]) {
						holdings.push(holding);
						if (this.ACT33TYPES[holding['customClientProperties']['legalStructure']]) {
							act33Holdings.push(holding);
						}
					}
				});
		});

		const holdingsCommunication = this.prospectusEmailService.getMailToLink(holdings);
		const act33HoldingsCommunication = this.prospectusEmailService.getMailToLink(act33Holdings);
		return {
			inAct33: act33Holdings.length > 0,
			holdingsCommunication: holdingsCommunication,
			act33HoldingsCommunication: act33HoldingsCommunication,
			disabledCommunication: !holdings.length
		};
	}

	// ------------------------
	// Generate Report
	// ------------------------

	public generateReportAsImages(enabled: boolean): void {
		this.exportAsPNG = true;
		this.generateReport(enabled);
	}

	public generateReport(enabled: boolean): void {
		//If showRiskProfileModularity = true and no models are selected from list, block report generation

		if (this.showRiskProfileModularity && this.reportConfigService.selectedModelIds?.length === 0) {
			this.errorPopupService.openResponseMessageDialog(
				PopupDialogObject.create({
					title: this.contentService.get('NO_PROFILES_SELECTED_DIALOG_HEADING', 'Error!'),
					description: this.contentService.get('NO_PROFILES_SELECTED_DIALOG_DESC', 'No model portfolios have been selected. Please select one or more profiles.'),
					showSpinner: false,
					actions: [
						PopupDialogAction.create({
							description: this.contentService.get('NO_PROFILES_SELECTED_DIALOG_BUTTON_DESC', ''),
							label: this.contentService.get('NO_PROFILES_SELECTED_DIALOG_BUTTON_LABEL', 'Close'),
							reload: false
						} as PopupDialogActionOptions)
					]
				} as PopupDialogOptions)
			);
			return;
		}

		// PROPOSAL PDF VALIDATION SHOULD BE DONE ON GENERATE REPORT CLICK
		// if the proposal summary section is included & purpose state is not updated, fail validation, otherwise continue with general validation
		if (
			this.reportType === ViewCategory.PROPOSAL_PDF
			&& this.snapshotVisibilityConfig?.sections?.find(section => section.nameKey === 'SNAPSHOT_REPORT_PROPOSAL_SUMMARY_TITLE')?.checked
			&& (!this.purposeState?.riskTolerance || !this.purposeState?.objective)
		) {
			this.showPurposeValidation = true;
			this.canGenerateReport = false;

		} else {
			if (!enabled) {
				this.showLogoError = !this.advisorLogo.trim();
				this.emailNotSentError = !this.emailSent && (this.includeYield || this.includeScenarioYield);
			} else {
				this.model.outputFlags = [
					new OutputFlag('includeScenarioYield', this.includeScenarioYield),
					new OutputFlag('includeYield', this.includeYield),
					new OutputFlag('showDollarAmountAsDefault', this.showDollarAmountAsDefault),
					new OutputFlag('includeBenchmark', this.includeBenchmark)
				];

				if (this.iqFundStatuses) {
					this.model.outputFlags.push(
						new OutputFlag('red', this.iqFundStatuses.get(StatusColour.RED.colour)),
						new OutputFlag('yellow', this.iqFundStatuses.get(StatusColour.YELLOW.colour)),
						new OutputFlag('green', this.iqFundStatuses.get(StatusColour.GREEN.colour))
					);
				}

				if (this.showCoBrandingDisclosure) {
					this.hasValidAdvisorLogo = Boolean(!this.disableAdvisorLogo && this.advisorLogo.trim());
					this.model.outputFlags.push(new OutputFlag('hasValidAdvisorLogo', this.hasValidAdvisorLogo));
				}

				this.model['advisorLogo'] = this.advisorLogo;
				const loadingSuffix = this.contentService.get('PNG_LOADING_SUFFIX', 'Please note image generation may take significantly longer than PDF generation.');
				const loadingTitle = 'Loading... ' + (this.exportAsPNG ? loadingSuffix : '');
				const loadingDialog = this.errorPopupService.openResponseMessageDialog(PopupDialogObject.create({ title: loadingTitle, showSpinner: true } as PopupDialogOptions));
				this.appStateStoreProxyService
					.getApplicationState()
					.pipe(first())
					.subscribe(appState =>
						this.composeReportByConfig(this.reportType, loadingDialog, appState)
					);
				this.storeService.upsertOne(this.storeConfig.reportConfig || { name: 'reportConfig' } as Config<any>,
					{ includeYield: false, includeScenarioYield: false, showDollarAmountAsDefault: false });
			}
		}
	}

	private composeReportByConfig(reportType: string, loadingDialog: DzModalRef, applicationState: ApplicationState): void {
		const { customizationMap, tealiumMap } = this.getCustomizationAndTealiumMap();
		this.trackGenerateEvent(tealiumMap);
		const reportConfig = this.reportConfigService.getReport(reportType, this.model.outputFlags, customizationMap);
		const model = this.overrideModel();
		this.reportComposer
			.composeReport(reportConfig, this.contentService, model, applicationState, this.componentDataMapping$)
			.pipe(untilDestroyed(this))
			.subscribe(
				pages => {
					if (!this.pdfDebug) {
						const report = new ReportData();
						report.tracking = new Map();
						report.svgPages = pages;
						report.fileName = this.constructFileName(this.model, this.useClientNameForFilename, this.exportAsPNG);
						if (reportType === ViewCategory.FA_PLUS_PDF) {
							report.tracking.set('engagementId', model.uniqueID);
						}
						this.reportGenerationService
							.generateReport(report, this.isLandscape, this.exportAsPNG)
							.pipe(first())
							.subscribe(done => {
								loadingDialog.close();
								if (done) {
									this.redirectToTopOfMainPage();
								}
							}, err => this.handlePdfError(err));
					} else {
						loadingDialog.close();
						this.reportPages = pages;
					}
				},
				err => {
					this.handlePdfError(err);
				}
			);
	}

	private redirectToTopOfMainPage(): void {
		this.urlRedirectService.redirectToMainPage(this.applicationName)
			.then((navSuccess: boolean) => {
				if (navSuccess) {
					UrlUtils.resetScroll();
				}
			});
	}

	private constructFileName(model: ClientOutput, useClientNameForFilename: boolean, exportAsPNG: boolean): string {
		const prefix = this.contentService.get('PDF_FILE_PREFIX', 'Advisor Center ');
		let filename = '';
		if (useClientNameForFilename && model['clientName']) {
			filename = model['clientName'];
			if (model['suite']) {
				filename += ' - ' + model['suite'];
			}
		} else {
			filename = model['nameOfEngagement'] ? model['nameOfEngagement'] : this.contentService.get('PDF_FILE_NAME', model.advisorName || 'Portfolio Report');
		}
		const date = dayjs().format('MMM DD YYYY');
		const fileType = exportAsPNG ? '.zip' : '.pdf';
		return this.sanitizeFileNameString(prefix + filename) + ' ' + date + fileType;
	}

	private sanitizeFileNameString(proposedFilename: string): string {
		if (typeof proposedFilename !== 'string') {
			proposedFilename = '';
		}
		const result = proposedFilename
			.replace(new RegExp('[^a-z0-9 _-]', 'gi'), ' ')
			.replace(new RegExp('[ ]+', 'gi'), ' ')
			.trim();

		return result || this.contentService.get('FALLBACK_FILE_NAME', 'Portfolio Report');
	}

	private handlePdfError(err): void {
		console.error('Error occurred', err);
		this.failedMessage.requestId = err?.headers?.get('x-request-id');
		this.errorPopupService.closeAllDialogs();
		this.errorPopupService.openResponseMessageDialog(this.failedMessage, true);
	}

	private trackGenerateEvent(tealiumMap: Map<string, { order: number; isVisible: string }>): void {
		let tealiumReportType;
		switch (this.reportType) {
			case ViewCategory.ADVISOR_PDF:
				tealiumReportType = TealiumConstants.TOOLS_EVENT.ADVISOR_PDF;
				break;
			case ViewCategory.CLIENT_PDF:
				tealiumReportType = TealiumConstants.TOOLS_EVENT.CLIENT_PDF;
				break;
			case ViewCategory.PROPOSAL_PDF:
				tealiumReportType = TealiumConstants.TOOLS_EVENT.PROPOSAL_PDF;
				break;
			default:
				tealiumReportType = TealiumConstants.TOOLS_EVENT.ADVISOR_PDF;
		}

		// sort map by order with null/undefined at the end
		const sortedTealiumMap = new Map(Array.from(tealiumMap).sort((a, b) => (!!a[1].order ? a[1].order : Infinity) - (!!b[1].order ? b[1].order : Infinity)));
		const reportEvent = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.DOWNLOAD, {
			reportType: tealiumReportType,
			reportVars: this.getTealiumReportVars(this.reportType),
			modularVars: this.includeCustomization ? this.getTealiumModularVars(tealiumReportType, sortedTealiumMap) : ''
		});
		this.tealiumAnalyticsService.trackEventTealium(reportEvent);

		if (tealiumReportType === TealiumConstants.TOOLS_EVENT.PROPOSAL_PDF) {
			const reportEvent = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.DOWNLOAD, {
				reportType: tealiumReportType,
				reportVars: [TealiumConstants.TOOLS_EVENT.PROPOSAL_PDF, `incSnapshotCustomisation:${this.includeSnapshotCustomization ? 'T' : 'F'}`].join('|'),
				modularVars: this.getTealiumModularVarsForProposalSummary(sortedTealiumMap, this.includeSnapshotCustomization)
			});

			this.tealiumAnalyticsService.trackEventTealium(reportEvent);
		}
	}

	private getTealiumReportVars(reportType: string): string {
		const varArray: string[] = [];
		varArray.push('incLogo:' + ((this.advisorLogo !== ' ' && !this.disableAdvisorLogo) ? 'T' : 'F'));
		varArray.push('incCustomization:' + (this.includeCustomization ? 'T' : 'F'));
		if (reportType === ViewCategory.CLIENT_PDF) {
			varArray.unshift('client report');
			varArray.push('incScenarioYield:' + (this.includeScenarioYield ? 'T' : 'F'));
			varArray.push('incYield:' + (this.includeYield ? 'T' : 'F'));
		} else if (reportType === ViewCategory.ADVISOR_PDF) {
			varArray.unshift('advisor report');
		} else if (reportType === ViewCategory.PROPOSAL_PDF) {
			varArray.unshift(TealiumConstants.TOOLS_EVENT.PROPOSAL_PDF);
		} else {
			varArray.unshift('advisor report');
		}

		if (this.isBenchmarkAvailable) {
			varArray.push('incBenchmark:' + (this.includeBenchmark ? 'T' : 'F'));
		}
		if (this.iqFundStatuses) {
			varArray.push(`incIqStatusRed:${this.iqFundStatuses.get(StatusColour.RED.colour)}`);
			varArray.push(`incIqStatusYellow:${this.iqFundStatuses.get(StatusColour.YELLOW.colour)}`);
			varArray.push(`incIqStatusGreen:${this.iqFundStatuses.get(StatusColour.GREEN.colour)}`);
		}
		return varArray.join('|');
	}

	private getTealiumModularVars(
		reportType: string,
		sortedTealiumMap: Map<string, { order: number; isVisible: string }>,
		tealiumConstants: Record<string, string> = TealiumConstants.PDF_SECTION_IDS
	): string {
		const varArray: string[] = [];
		if (sortedTealiumMap.size) {
			varArray.push(reportType);
			sortedTealiumMap.forEach((value, key) => {
				if (tealiumConstants[key]) {
					const trackingString = tealiumConstants[key] + ':' + value.isVisible;
					varArray.push(trackingString);
				} else {
					console.warn('Warning! ' + key + ' is not being tracked. Please add it to the TealiumConstants file.');
				}
			});
		}
		return varArray.join('|');
	}

	private getTealiumModularVarsForProposalSummary(sortedTealiumMap: Map<string, { order: number; isVisible: string }>, includeSnapshotCustomization: boolean): string {
		const varArray: string[] = [];

		varArray.push(TealiumConstants.TOOLS_EVENT.PROPOSAL_PDF);

		Object.entries(this.purposeState).forEach(([statementType, value]) => {
			varArray.push(`${statementType}:${value}`);
		}, varArray);

		if (sortedTealiumMap.size && includeSnapshotCustomization) {
			sortedTealiumMap.forEach((value, key) => {
				if (key.startsWith(TealiumConstants.PDF_CLIENT_PROPOSAL_SECTION_ID_PREFIX)) {
					const trackingString = camelCase('summary' + key.substring(15)) + ':' + value.isVisible;
					varArray.push(trackingString);
				}
			});
		}
		return varArray.join('|');
	}

	// ------------------------
	// Actions
	// ------------------------

	public onCancel(): void {
		this.storeService.upsertOne(
			this.storeConfig.reportConfig || { name: 'reportConfig' } as Config<any>,
			{ includeYield: false, includeScenarioYield: false, showDollarAmountAsDefault: false }
		);
		const event = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.CLOSE_REPORT_WINDOW, {});
		this.tealiumAnalyticsService.trackEventTealium(event);
		this.redirectToTopOfMainPage();
	}

	// ------------------------
	// On Updates
	// ------------------------

	public onAdvisorLogoUpdate(value: string): void {

		// Whitespace is needed in case the advisor logo is empty string because otherwise the pdf generation call to
		// ACServer fails.
		const logoDataUrl = SvgSanitizer.sanitizeImageDataUrl(value);
		if (this.showCoBrandingDisclosure) {
			this.advisorLogo = logoDataUrl ? `<image preserveAspectRatio="xMinYMid meet" x="48" y="20" width="240" height="80" id="Bitmap" xlink:href="${logoDataUrl}"></image>` : ' ';
		} else {
			this.advisorLogo = logoDataUrl ? `<image preserveAspectRatio="xMinYMid meet" x="36" y="57" width="160" height="43.2786885" id="Bitmap" xlink:href="${logoDataUrl}"></image>` : ' ';
		}
		this.model['advisorLogo'] = this.advisorLogo;
		this.firmNameRequired = !this.advisorLogo.trim();
		this.showLogoError = false;

		const model = this.overrideModel();
		this.reportCover = this.reportConfigService.getReportCover(this.reportType, model);
	}

	public onAdvisorLogoToggle(value: boolean): void {
		this.disableAdvisorLogo = value;
		const model = this.overrideModel();
		this.firmNameRequired = value;
		this.reportCover = this.reportConfigService.getReportCover(this.reportType, model);
		this.clear();
	}

	public onConfigToggle(value: boolean): void {
		this.includeCustomization = value;
	}

	public onSnapshotConfigToggle(value: boolean): void {
		this.includeSnapshotCustomization = value;
	}

	public onIncludeYieldChange(value: boolean): void {
		this.includeYield = value;
		this.clear();
	}

	public onIncludeScenarioYieldChange(value: boolean): void {
		this.includeScenarioYield = value;
		this.clear();
	}

	public onIncludeBenchmarkChange(value: boolean): void {
		this.includeBenchmark = value;
		this.clear();
	}

	public toggleGenerateDrawer(): void {
		this.showGenerateActions = !this.showGenerateActions;
	}
	public onSendEmail(): void {
		this.emailSent = true;
		this.clear();
	}

	public onFormChange(value: NgForm): void {
		this.inputForm = value;
		this.clear();
	}

	public onConfigChange(config: PdfReportConfig): void {
		this.reportVisibilityConfig = config;
		if (this.applicationName === ApplicationNames.MODELS_BROWSE) {
			this.clear();
		}
	}

	public onSnapshotConfigChange(config: PdfReportConfig): void {
		this.snapshotVisibilityConfig = config;
	}

	public setSelectedModelIds(event: string[]): void {
		this.reportConfigService.selectedModelIds = event;
	}

	public onFundStatusChange($event: Map<string, boolean>): void {
		this.iqFundStatuses = $event;
	}

	public onProposalLoadingCompletion(): void {
		this.canGenerateReport = this.checkGenerateReport();
	}

	// ------------------------
	// Validation
	// ------------------------

	private overrideModel(): ClientOutput {
		const override =
			this.advisorLogo !== ' ' ?
				this.disableAdvisorLogo ?
					{ advisorLogo: ' ', coverPageFirm: this.model.firm } : { firm: get(this.model, 'firm', ' '), coverPageFirm: '' }
				: { advisorLogo: ' ', coverPageFirm: this.model.firm };

		return Object.assign({}, this.model, override);
	}

	private requiredActionPerformed(): boolean {
		if (this.yieldConfig && this.showFundProspectus) {
			if (this.yieldConfig['inAct33']) {
				return this.emailSent;
			} else if (this.yieldConfig['disabledCommunication']) {
				return true;
			} else {
				return this.includeYield || this.includeScenarioYield ? this.emailSent : true;
			}
		}
		return true;
	}

	public isEmailProspectusRequiredAndValid(): boolean {
		if (!this.showFundProspectus) {
			return false;
		}

		const isActionRequired: boolean = this.includeYield || this.includeScenarioYield || get(this.yieldConfig, `['inAct33']`);
		const isSkippingAction: boolean = get(this.yieldConfig, `['disabledCommunication']`);
		const isEmailValid: boolean = !this.isEmailProspectusInvalid();
		return isActionRequired && !isSkippingAction && isEmailValid;
	}

	public isEmailProspectusInvalid(): boolean {
		if (this.yieldConfig) {
			const isHoldingEmailInvalid: boolean = !!this.includeYield && !!this.includeScenarioYield && !this.yieldConfig['holdingsCommunication'];
			const isAct33EmailInvalid: boolean = !this.includeYield && !!this.yieldConfig['inAct33'] && !this.yieldConfig['act33HoldingsCommunication'];
			return isHoldingEmailInvalid || isAct33EmailInvalid;
		}
		return true;
	}

	// ------------------------
	// Setters
	// ------------------------

	private setApplicationText(): void {
		switch (this.applicationName) {
			case ApplicationNames.PORTFOLIO_ANALYSER:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_PA_APPLICATION_NAME', '360&deg; EVALUATOR') + ' - ';
				break;
			case ApplicationNames.SCENARIO_TESTER:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_ST_APPLICATION_NAME', 'SCENARIO TESTER') + ' - ';
				break;
			case ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_PAC_APPLICATION_NAME', 'PORTFOLIO_ANALYSIS_CONSTRUCTION') + ' - ';
				this.isPacApp = true;
				break;
			case ApplicationNames.PORTFOLIO_CENTRE:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_PC_APPLICATION_NAME', 'PORTFOLIO CENTRE') + ' - ';
				break;
			case ApplicationNames.PORTFOLIO_CENTRE_360:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_PC_360_APPLICATION_NAME', 'PORTFOLIO CENTRE') + ' - ';
				break;
			case ApplicationNames.IRETIRE_ACCUM:
			case ApplicationNames.IRETIRE_DECUM:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_IRETIRE_APPLICATION_NAME', 'iRETIRE') + ' - ';
				break;
			case ApplicationNames.INVESTMENT_QUALITY:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_IQ_APPLICATION_NAME', 'INVESTMENT QUALITY') + ' - ';
				// TODO: to show portfolio id or additional header for IQ pdf
				this.showAdditionalHeader = true;
				break;
			case ApplicationNames.MODELS_EXPERIENCE:
				this.applicationNameText = this.contentService.get('PDF_GENERATE_MB_APPLICATION_NAME', 'ALLOCATION & INSIGHTS') + ' - ';
				this.appStateStoreProxyService
					.getApplicationState()
					.pipe(first())
					.subscribe(appState => {
						this.additionalHeaderInfo = appState?.generateReportDataState?.additionalHeaderInfo;
						this.showAdditionalHeader = true;
					});
				break;
		}
	}

	private getCustomizationAndTealiumMap(): any {
		const customizationMap: Map<string, { order: number; isVisible: boolean }> = new Map();
		const tealiumCustomizationMap: Map<string, { order: number; isVisible: string }> = new Map();

		if (this.reportVisibilityConfig) {
			this.updateCustomisationMap(this.reportVisibilityConfig, customizationMap, tealiumCustomizationMap);
		}
		// add snapshot specific subsections 
		if (this.snapshotVisibilityConfig) {
			this.updateCustomisationMap(this.snapshotVisibilityConfig, customizationMap, tealiumCustomizationMap);
		}
		return { customizationMap: customizationMap, tealiumMap: tealiumCustomizationMap };
	}

	private updateCustomisationMap(
		visibilityConfig: PdfReportConfig,
		customizationMap: Map<string, { order: number; isVisible: boolean }>,
		tealiumCustomizationMap: Map<string, { order: number; isVisible: string }>
	): void {
		visibilityConfig.sections.forEach(section => {
			customizationMap.set(section.nameKey, { order: section.order, isVisible: section.checked });
			if (section.visible) {
				tealiumCustomizationMap.set(section.nameKey, { order: section.order, isVisible: section.checked ? 'T' : 'F' });
			}
			if (section.subsections) {
				section.subsections.forEach(subsection => {
					subsection.componentNames.forEach(componentName => {
						customizationMap.set(componentName, { order: null, isVisible: subsection.checked });
						if (subsection.visible) {
							tealiumCustomizationMap.set(componentName, { order: null, isVisible: subsection.checked ? 'T' : 'F' });
						}
					});
				});
			}
		});
	}

	private isFamilyIdEnabledForRiskProfileModularity(): boolean {
		const familyId = this.urlService.getModelFamilyId();
		return this.pdfRiskProfileModularityFamiliesList.includes(familyId);
	}
}
